import React from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme, styled } from '@mui/material/styles';
import MuiDialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide, { SlideProps } from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { ButtonClose } from '../../../shared/material_components/types';
import {
  fullScreenDialogSx, legacyPaperStyle, legacyActionSyle,
  topPaperScrollBody, topScrollPaper
} from './modal.styles';
import type {
  ExtendedTitleProps, Props, OmitProps, ExtendedContentProps
} from './modal.types'


export const modalMaxWidth = (
  maxWidth: DialogProps['maxWidth'], legacyMode?: boolean
) => {
  if (maxWidth) {
    return maxWidth;
  }
  if (legacyMode) {
    return 'sm';
  }
  return 'md';
};

export const Transition = React.forwardRef(
  (props: SlideProps & TransitionProps & {
    children: React.ReactElement<any, any>;
  }, ref: React.Ref<unknown>
  ) => <Slide direction='down' ref={ref} {...props} />
);

export const slideTopAndLegacyTransition = (slideTop?: boolean, legacyMode?: boolean) =>
  slideTop ? {
        TransitionComponent: Transition,
        scroll: 'paper' as 'paper',
        sx: {
          '& .MuiPaper-root': legacyMode ? legacyPaperStyle : {},
          '& .MuiDialogActions-root': legacyMode
            ? legacyActionSyle
            : { padding: (theme: Theme) => theme.spacing(1) },
          '& .MuiDialog-scrollPaper': slideTop ? topScrollPaper : {},
          '& .MuiDialog-paperScrollBody': slideTop ? topPaperScrollBody : {},
        },
      }
    : {};

export const StyledDialog = styled(Dialog)(({ fullScreen }) => ({
  '& .MuiPaper-root': fullScreen ? fullScreenDialogSx : {},
  zIndex: 1300,
}));

export const StyledDialogContent = styled(MuiDialogContent)({
  display: 'flex',
  flexDirection: 'column',
});

export const StyledModal = (props: Omit<Props, OmitProps>) => {
  const { legacyMode, slideTop, ...rest } = props;

  return (
    <StyledDialog
      {...rest}
      fullWidth
      maxWidth={modalMaxWidth(rest.maxWidth, legacyMode)}
      {...slideTopAndLegacyTransition(slideTop, legacyMode)}
    >
      {rest.children}
    </StyledDialog>
  );
};

export const StyledModalContent = (props: ExtendedContentProps) => {
  const { legacyMode, ...rest } = props;

  return (
    <StyledDialogContent {...rest} sx={{ padding: legacyMode ? 0 : {} }}>
      {rest.children}
    </StyledDialogContent>
  );
};

export const StyledDialogTitle = (props: ExtendedTitleProps) => {
  const { children, onClose, legacyMode } = props;

  return (
    <>
      {legacyMode ? (
        <Typography variant='h4' sx={{ marginBottom: 1.25 }}>
          {children}
        </Typography>
      ) : (
        <DialogTitle>
          {children}
          {onClose ? (
            <IconButton
              aria-label='close'
              onClick={onClose as ButtonClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
      )}
    </>
  );
};